import React, { useEffect, useState, useRef, useCallback } from "react";
import DataTableBase from "../../../Components/Common/DataTableBase/DataTableBase";
import { t } from "i18next";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  UncontrolledTooltip,
  Spinner,
} from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import { useConfirmDialogStore } from "../../../stores/Modal/ConfirmDialogStore";
import { useSpaceStore } from "../../../stores/Assets/space";
import AddSpace from "./Components/AddSpace";
import EditSpace from "./Components/EditSpace";
import { useSiteStore } from "../../../stores/Assets/site";
import { useMenuStore } from "../../../stores/Assets/menu";
import { useKitchenStore } from "../../../stores/Assets/kitchen";
import { QRCode } from "react-qrcode-logo";
import QRmodal from "./Components/QRmodal";
import DownloadQRModal from "./Components/DownloadQRModal";
import ExportQRPdfModal from "./Components/ExportQRPdfModal";
import SplashCustomizationModal from "./Components/SplashCustomizationModal";
import { Pagination } from "../../../Components/Common/DataTableBase/Pagination";
import { set } from "lodash";
import { Permission } from "../../../Layouts/Permissions";
import { canEdit } from "../../../helpers/permissions";
import SearchBar from "../../../Components/Common/SearchBar";
import { axiosHelper, tokenHelper } from "../../../stores/helpers";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Skeleton from "../../../Components/Common/Skeleton";
import { canFullOrder } from "../../../helpers/planLimits";
import { useUsageStore } from "../../../stores/Usage";
import { PlanUpgradeModal } from "../../../Components/Common/PlanUpgradeModal";

const Spaces = () => {
  const title = "BASSEER | SPACES";
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sites, setSites] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [menus, setMenus] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("All");
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [selectedKitchenId, setSelectedKitchenId] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [selectedRowQR, setSelectedRowQR] = useState(null);
  const { getSpaces, isLoading, spaces, deleteSpace, pagination } =
    useSpaceStore((state) => state);
  const { getSites } = useSiteStore((state) => state);
  const { getMenus } = useMenuStore((state) => state);
  const { getKitchens } = useKitchenStore((state) => state);
  const { fetchUsage, getLimitInfo, usage } = useUsageStore();
  const spaceLimit = getLimitInfo("spaces");
  const isLimitReached = spaceLimit?.isReached;
  const canSplash = usage?.limits?.can_splash;
  const fullOrderEnabled = canFullOrder(usage);

  const location = useLocation();
  const [loaded, setLoaded] = useState(true);
  useEffect(() => {
    const fetchSites = async () => {
      const siteList = await getSites();
      setSites(siteList);

      const urlParams = new URLSearchParams(location.search);
      const siteIdFromUrl = urlParams.get("siteId");

      if (siteIdFromUrl) {
        setSelectedSiteId(siteIdFromUrl);
      }
      // Otherwise keep default "All" selected
    };

    const fetchMenus = async () => {
      const menuList = await getMenus(null, null, 500);
      setMenus(menuList?.menus);

      if (menuList?.length > 0) {
        setSelectedMenuId(menuList[0].menu_id);
      }
    };

    const fetchKitchens = async () => {
      const kitchenList = await getKitchens({ page: 1, perPage: 500 });

      setKitchens(kitchenList.kitchens);

      if (kitchenList?.length > 0) {
        setSelectedKitchenId(kitchenList[0].kitchen_id);
      }
    };
    setLoaded(true);
    fetchUsage();
    fetchSites();
    fetchMenus();
    fetchKitchens();
    setLoaded(false);
  }, [getSites, location.search, getSpaces]);

  useEffect(() => {
    setTotalRows(pagination?.totalItems || 0);
  }, [pagination]);

  const handleQRClick = (row) => {
    console.log(row);

    setSelectedRowQR(row);
    setShowQRCode(!showQRCode);
  };

  const handleSplashClick = (row) => {
    setSelectedSplashRow(row);
    setShowSplashModal(true);
  };

  const toggleSplashModal = () => {
    setShowSplashModal(!showSplashModal);
  };

  const columns = [
    {
      name: t("ID"),
      selector: (row) => row?.space_id,
      width: "70px",
      sortable: true,
      wrap: true,
    },
    {
      name: t("Site"),
      width: "150px",
      selector: (row) => `${row?.site_name} (id: ${row?.site_id})`,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Name"),
      selector: (row) => row?.name,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Name (AR)"),
      selector: (row) => row?.name_ar,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Order Distance"),
      selector: (row) => row?.allowedOrderDistance,
      sortable: true,
      wrap: true,
      omit: !fullOrderEnabled,
    },
    {
      name: t("Type"),
      selector: (row) => row?.type,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Default LANG"),
      selector: (row) => row?.default_lang,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Menu"),
      selector: (row) => row?.menu_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div>{row.menu_id && `${row.menu_name} (id: ${row.menu_id})`}</div>
      ),
    },
    {
      name: t("Station"),
      selector: (row) => row?.kitchen_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div>
          {row.kitchen_id && `${row.kitchen_name} (id: ${row.kitchen_id})`}
        </div>
      ),
    },
    {
      name: t("Secondary Station"),
      selector: (row) => row?.secondary_kitchen_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div>
          {row.secondary_kitchen_id &&
            `${row.secondary_kitchen_name} (id: ${row.secondary_kitchen_id})`}
        </div>
      ),
    },
    {
      name: t("QR Code"),
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="cursor-pointer">
          <span
            style={{ fontSize: "14px" }}
            className="badge bg-soft-success cursor-pointer text-success text-uppercase"
            onClick={() => handleQRClick(row)}
          >
            <i className="ri-external-link-line"></i> View
          </span>
        </div>
      ),
    },
    {
      name: t("SubSpaces"),
      sortable: true,
      wrap: true,
      cell: (row) => (
        <a
          href={`/subspaces/${row?.space_id}`}
          className="cursor-pointer"
          id={`anchor-${row?.space_id}`}
        >
          <span
            style={{ fontSize: "14px" }}
            className="badge bg-soft-info cursor-pointer text-success text-uppercase"
          >
            <i className="ri-external-link-line"></i> List
          </span>
          <UncontrolledTooltip
            placement="top"
            target={`anchor-${row?.space_id}`}
          >
            Check Associated Spaces
          </UncontrolledTooltip>
        </a>
      ),
    },
    {
      name: t("Save Last Order"),
      selector: (row) => row?.saveLastOrder,
      sortable: true,
      wrap: true,
      omit: !fullOrderEnabled,
      cell: (row) => (
        <div>
          {row?.saveLastOrder !== undefined ? (
            <span
              className={`badge ${
                row?.saveLastOrder ? "bg-success" : "bg-danger"
              }`}
            >
              {row?.saveLastOrder ? "Yes" : "No"}
            </span>
          ) : (
            <span className="badge bg-secondary">N/A</span>
          )}
        </div>
      ),
    },
    {
      name: t("Splash"),
      selector: (row) => row?.has_splash,
      sortable: true,
      wrap: true,
      omit: !canSplash,
      cell: (row) => (
        <div className="cursor-pointer">
          <span
            style={{ fontSize: "14px" }}
            className={`badge cursor-pointer text-uppercase ${
              row?.call_active
                ? "bg-soft-success text-success"
                : "bg-soft-secondary text-secondary"
            }`}
            onClick={() => handleSplashClick(row)}
          >
            {row?.call_active ? "Yes" : "No"}
          </span>
        </div>
      ),
    },
  ];

  const { showConfirm } = useConfirmDialogStore((state) => state);
  const [showAddSpaceModal, setShowAddSpaceModal] = useState(false);
  const [showEditSpaceModal, setShowEditSpaceModal] = useState(false);
  const [showDownloadQRModal, setShowDownloadQRModal] = useState(false);
  const [showSplashModal, setShowSplashModal] = useState(false);
  const [selectedSplashRow, setSelectedSplashRow] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const toggleUpgradeModal = () => setShowUpgradeModal(!showUpgradeModal);

  const toggleAddSpaceModal = () => {
    if (!showAddSpaceModal && isLimitReached) {
      setShowUpgradeModal(true);
      return;
    }
    setShowAddSpaceModal(!showAddSpaceModal);
  };

  const toggleDownloadQRModal = () => {
    setShowDownloadQRModal(!showDownloadQRModal);
  };

  const [selectedRow, setSelectedRow] = useState(null);
  const toggleEditSpaceModal = (row) => {
    setSelectedRow(row);
    setShowEditSpaceModal(!showEditSpaceModal);
  };

  const deleteSpaceFun = async (id) => {
    try {
      await deleteSpace(id, selectedSiteId);
      fetchUsage();
    } catch (e) {
      console.log(e);
    }
  };

  const handleSiteSelect = (e) => {
    const id = e.target.value;
    setSelectedSiteId(id);
    // The useEffect will handle fetching based on the new selectedSiteId
  };

  const [currentSpaces, setCurrentSpaces] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    if (spaces?.length > 0) {
      const curr = spaces.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage,
      );
      setCurrentSpaces(curr);
    }
  }, [currentPage, spaces]);

  const onPageChange = (page) => {
    setCurrentPage(page);
  };
  const spacesFetchRequestId = useRef(0);

  const fetchAllSpaces = async (query) => {
    const requestId = ++spacesFetchRequestId.current;
    try {
      useSpaceStore.setState({ isLoading: true });
      const accessToken = await tokenHelper.getToken();
      const params = new URLSearchParams({
        page: query.page,
        perPage: query.perPage,
      });
      if (query.search && query.search.trim() !== "") {
        params.append("search", query.search.trim());
      }

      const spaceResponse = await axiosHelper.get(
        `/v2/space?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      if (requestId !== spacesFetchRequestId.current) return;

      const combinedSpaces =
        spaceResponse.spaces?.map((space) => {
          const firstMenu = space.menus?.[0];
          return {
            ...space,
            site_name: space.site?.name || "",
            site_name_ar: space.site?.name_ar || "",
            site_id: space.site?.site_id || space.site_id,
            menu_site_image: space.site?.image_url || "",
            kitchen_name: space.kitchen?.name || "",
            kitchen_name_ar: space.kitchen?.name_ar || "",
            kitchen_id: space.kitchen?.kitchen_id || space.kitchen_id,
            secondary_kitchen_id:
              space.secondary_kitchen_id ||
              space.secondaryKitchen?.kitchen_id ||
              null,
            secondary_kitchen_name:
              space.secondary_kitchen_name ||
              space.secondaryKitchen?.kitchen?.name ||
              "",
            menu_ids: (space.menus || []).map((m) => m.menu_id),
            menu_id: firstMenu?.menu_id,
            menu_name: firstMenu?.name,
            qrKey: space.qrcodes?.[0]?.key || null,
          };
        }) || [];

      useSpaceStore.setState({
        spaces: combinedSpaces,
        pagination: spaceResponse.pagination,
        isLoading: false,
      });
    } catch (error) {
      if (requestId !== spacesFetchRequestId.current) return;
      console.error("Error fetching all spaces:", error);
      useSpaceStore.setState({ isLoading: false });
    }
  };

  const onChangePage = (page) => {
    if (selectedSiteId && selectedSiteId !== "All") {
      getSpaces(selectedSiteId, {
        page: page,
        perPage: 10,
        search: searchTerm,
      });
    } else if (selectedSiteId === "All") {
      fetchAllSpaces({ page: page, perPage: 10, search: searchTerm });
    }
  };

  const searchHandler = (searchText) => {
    setSearchTerm(searchText);
    setCurrentPage(1);
  };

  // Refetch when committed searchTerm or selectedSiteId changes
  useEffect(() => {
    if (selectedSiteId && selectedSiteId !== "All") {
      // Invalidate any in-flight All-sites fetch
      spacesFetchRequestId.current += 1;
      getSpaces(selectedSiteId, { page: 1, perPage: 10, search: searchTerm });
    } else if (selectedSiteId === "All") {
      // Invalidate any in-flight site-scoped fetch
      useSpaceStore.setState({
        _spacesListRequestId:
          (useSpaceStore.getState()._spacesListRequestId || 0) + 1,
      });
      fetchAllSpaces({ page: 1, perPage: 10, search: searchTerm });
    }
  }, [searchTerm, selectedSiteId]);

  const [canEditspace, setCanEditspace] = useState(false);
  const [isDownloadingQR, setIsDownloadingQR] = useState(false);
  const [allFilteredSpaces, setAllFilteredSpaces] = useState([]);
  const [showExportPdfModal, setShowExportPdfModal] = useState(false);
  const qrRefs = useRef({});

  const toggleExportPdfModal = () => {
    setShowExportPdfModal((prev) => !prev);
  };

  useEffect(() => {
    const checkPermission = async () => {
      const res = await canEdit("space");
      setCanEditspace(res);
    };

    checkPermission();
  }, []);

  // Fetch ALL filtered spaces for QR download (uses dedicated endpoint)
  const fetchAllFilteredSpaces = useCallback(async (options = {}) => {
    try {
      const accessToken = await tokenHelper.getToken();
      const params = new URLSearchParams();

      // Add site filter
      if (selectedSiteId) {
        params.append("siteId", selectedSiteId);
      }

      // Add search filter
      if (searchTerm && searchTerm.trim() !== "") {
        params.append("search", searchTerm.trim());
      }

      if (options.includeSubspaces) {
        params.append("includeSubspaces", "true");
      }

      // Use the dedicated QR download endpoint
      const response = await axiosHelper.get(
        `/v2/space/qr-download/all?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      // Response is already formatted with qrKey and site_image
      return response || [];
    } catch (error) {
      console.error("Error fetching spaces for QR download:", error);
      return [];
    }
  }, [selectedSiteId, searchTerm]);

  // Convert QR canvas to blob
  const convertQRToBlob = async (qrElement) => {
    return new Promise((resolve) => {
      const canvas = qrElement.querySelector("canvas");
      if (canvas) {
        try {
          canvas.toBlob((blob) => {
            resolve(blob);
          }, "image/png");
        } catch (error) {
          console.error("Error converting canvas to blob:", error);
          try {
            const dataUrl = canvas.toDataURL("image/png");
            fetch(dataUrl)
              .then((res) => res.blob())
              .then((blob) => resolve(blob))
              .catch(() => resolve(null));
          } catch (e) {
            console.error("Fallback failed:", e);
            resolve(null);
          }
        }
      } else {
        resolve(null);
      }
    });
  };

  // Create CSV file with space name and QR URL (Excel-compatible)
  const createXLSXFile = (spaces) => {
    // Create proper CSV content that Excel can read
    const headers = [
      "Space Name",
      "Service Station",
      "QR URL",
      "Space ID",
      "File Name",
    ];
    const csvContent = [
      headers.join(","),
      ...spaces.map((space) =>
        [
          `"${(space.name || "").replace(/"/g, '""')}"`,
          `"${(space.kitchen_name || "").replace(/"/g, '""')}"`,
          // Escape quotes in CSV
          `"${("https://qr.clickorder.io" + "/menu/" + space.qrKey || "").replace(/"/g, '""')}"`, // Escape quotes in CSV
          `"${space.space_id || ""}"`,
          `"${`${space.name} - ${space.space_id}.png`.replace(/"/g, '""')}"`,
        ].join(","),
      ),
    ].join("\n");

    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = "\uFEFF";
    return BOM + csvContent;
  };

  // Download all filtered QR codes
  const downloadAllFilteredQRs = async () => {
    setIsDownloadingQR(true);

    try {
      // Fetch all filtered spaces
      const allSpaces = await fetchAllFilteredSpaces();

      if (allSpaces?.length === 0) {
        alert(t("No spaces with QR codes found"));
        setIsDownloadingQR(false);
        return;
      }

      // Store all spaces for QR rendering
      setAllFilteredSpaces(allSpaces);

      // Wait for QR codes to render
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const zip = new JSZip();
      let successCount = 0;

      for (const space of allSpaces) {
        const qrElement = qrRefs.current[space.space_id];
        if (!qrElement) continue;

        const blob = await convertQRToBlob(qrElement);
        if (blob) {
          const fileName = `${space.name} - ${space.space_id}.png`;
          zip.file(fileName, blob);
          successCount++;
        }
      }

      // Add CSV file with space names and QR URLs
      const csvContent = createXLSXFile(allSpaces);
      zip.file("QR_Codes_List.csv", csvContent);

      if (successCount === 0) {
        alert(t("Failed to generate any QR codes. Please try again."));
        setAllFilteredSpaces([]); // Clear after failure
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const siteName =
        selectedSiteId === "All"
          ? "All Sites"
          : sites.find((s) => s.site_id === parseInt(selectedSiteId))?.name;
      const zipFileName = `${siteName || "QR_Codes"}_${new Date().getTime()}.zip`;
      saveAs(zipBlob, zipFileName);

      if (successCount < allSpaces?.length) {
        alert(
          t(
            `Successfully downloaded ${successCount} out of ${allSpaces.length} QR codes.`,
          ),
        );
      }

      // Clear the QR codes after successful download
      setAllFilteredSpaces([]);
    } catch (error) {
      console.error("Error downloading QR codes:", error);
      alert(t("Failed to download QR codes. Please try again."));
      setAllFilteredSpaces([]); // Clear after error
    } finally {
      setIsDownloadingQR(false);
    }
  };

  return (
    <>
      {sites?.length === 0 && loaded ? (
        <Modal isOpen={true}>
          <ModalHeader>No Registered Site</ModalHeader>
          <ModalBody>
            <div className="text-center">
              <p className="text-danger">{t("You should first add a site.")}</p>
              <Link to="/sites" className="btn btn-primary">
                {t("Add a Site")}
              </Link>
            </div>
          </ModalBody>
        </Modal>
      ) : kitchens.length === 0 && loaded ? (
        <Modal isOpen={true}>
          <ModalHeader>No Registered Kitchen</ModalHeader>
          <ModalBody>
            <div className="text-center">
              <p className="text-danger">
                {t("You should first add a kitchen.")}
              </p>
              <Link to="/kitchens" className="btn btn-primary">
                {t("Add Kitchen")}
              </Link>
            </div>
          </ModalBody>
        </Modal>
      ) : (
        <>
          {selectedSiteId && (
            <DataTableBase
              tableTitle={"SPACES"}
              customHeader={
                <div className="w-100">
                  {loaded ? (
                    // Skeleton loading state
                    <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
                      <div className="w-100" style={{ flex: "0 0 10%" }}>
                        <Skeleton variant="button" />
                      </div>
                      <div
                        className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2"
                        style={{ flex: "0 0 60%" }}
                      >
                        <div className="d-flex align-items-center gap-2 w-100">
                          <h6 className="text-nowrap mb-0">Search: </h6>
                          <div className="flex-grow-1">
                            <Skeleton variant="input" />
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 w-100">
                          <h6 className="text-nowrap mb-0">Site: </h6>
                          <div className="flex-grow-1">
                            <Skeleton variant="input" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
                      {canEditspace ? (
                        <Permission level={2} feature="space">
                          <Button
                            color={isLimitReached ? "secondary" : "success"}
                            onClick={toggleAddSpaceModal}
                            className="d-flex align-items-center justify-content-center w-100"
                            style={{
                              flex: "0 0 10%",
                              padding: "0.4rem 2rem",
                              whiteSpace: "nowrap",
                              fontSize: "13px",
                            }}
                            id="add-space-btn"
                          >
                            + {t("Add Space")}
                          </Button>
                          {isLimitReached && (
                            <UncontrolledTooltip target="add-space-btn">
                              {t("Limit reached. Upgrade to add more.")}
                            </UncontrolledTooltip>
                          )}
                        </Permission>
                      ) : null}
                      <div
                        className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2"
                        style={{ flex: "0 0 60%" }}
                      >
                        <div className="d-flex align-items-center gap-2 w-100">
                          <h6 className="text-nowrap mb-0">Search: </h6>
                          <div className="flex-grow-1">
                            <SearchBar
                              submitMode
                              onSearch={searchHandler}
                              placeholder="Search spaces by name, ID, or QR key..."
                            />
                          </div>
                        </div>
                        {sites.length > 0 && (
                          <div className="d-flex align-items-center gap-2 w-100">
                            <h6 className="text-nowrap mb-0">Select Site: </h6>
                            <select
                              className="form-control flex-grow-1"
                              disabled={isLoading}
                              value={selectedSiteId}
                              onChange={handleSiteSelect}
                            >
                              <option key="all" value="All">
                                All Sites
                              </option>
                              {sites.map((site) => (
                                <option key={site.site_id} value={site.site_id}>
                                  {site.site_id} - {site.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              }
              data={spaces}
              columns={columns}
              loading={isLoading}
              paginationTotalRows={totalRows}
              onChangePage={onChangePage}
              onHeaderAddBtnClick={null}
              onSearchIconClick={() => {}}
              actionColWidth="100px"
              showSearch={false}
              showActionColumn={canEditspace}
              showSubHeader={false}
              showActionButtons={false}
              paginationLeftContent={
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    onClick={downloadAllFilteredQRs}
                    disabled={
                      isLoading || spaces.length === 0 || isDownloadingQR
                    }
                    style={{ backgroundColor: "#c59253" }}
                    className="btn-sm text-white"
                  >
                    {isDownloadingQR ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {t("Downloading...")}
                      </>
                    ) : (
                      <>
                        <i className="ri-qr-code-line me-2"></i>
                        {t("Download QR Codes")}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={toggleExportPdfModal}
                    disabled={
                      isLoading || spaces.length === 0 || isDownloadingQR
                    }
                    outline
                    className="btn-sm"
                    style={{ borderColor: "#c59253", color: "#c59253" }}
                  >
                    <i className="ri-file-pdf-line me-2"></i>
                    {t("Export PDF")}
                  </Button>
                </div>
              }
              customActionBtns={(row) => (
                <>
                  <div className="flex gap-2">
                    <Permission level={2} feature="space">
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => toggleEditSpaceModal(row)}
                        title="Edit"
                      >
                        <i className="ri-edit-fill"></i>
                      </button>
                    </Permission>
                    <Permission level={2} feature="space">
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          showConfirm(
                            () => deleteSpaceFun(row?.space_id), // confirmCB
                            () => {}, // cancelCB (optional)
                            t("Confirm Delete"), // title
                            t(
                              `Are you sure you want to delete Space ${row?.name} (id: ${row?.space_id})?`,
                            ), // question
                          );
                        }}
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </Permission>
                  </div>
                </>
              )}
            />
          )}

          {showAddSpaceModal && (
            <AddSpace
              siteID={selectedSiteId}
              siteList={sites}
              menuList={menus}
              kitchenList={kitchens}
              toggleAddSpaceModal={toggleAddSpaceModal}
              showAddSpaceModal={showAddSpaceModal}
            />
          )}
          {selectedRow && (
            <EditSpace
              siteList={sites}
              menuList={menus}
              kitchenList={kitchens}
              rowData={selectedRow}
              toggleEditSpaceModal={toggleEditSpaceModal}
              showEditSpaceModal={showEditSpaceModal}
            />
          )}
          {selectedRowQR && (
            <QRmodal
              altImage={
                sites.find((s) => s.site_id === parseInt(selectedSiteId))
                  ?.image_url
              }
              info={selectedRowQR}
              showQRCode={showQRCode}
              toggleQRModal={() => setShowQRCode(!showQRCode)}
            />
          )}
          <DownloadQRModal
            isOpen={showDownloadQRModal}
            toggle={toggleDownloadQRModal}
            spaces={spaces}
            siteName={
              selectedSiteId === "All"
                ? "All Sites"
                : sites.find((s) => s.site_id === parseInt(selectedSiteId))
                    ?.name
            }
            siteImage={
              selectedSiteId === "All"
                ? null
                : sites.find((s) => s.site_id === parseInt(selectedSiteId))
                    ?.image_url
            }
          />
          <ExportQRPdfModal
            isOpen={showExportPdfModal}
            toggle={toggleExportPdfModal}
            fetchSpaces={fetchAllFilteredSpaces}
            siteName={
              selectedSiteId === "All"
                ? "All Sites"
                : sites.find((s) => s.site_id === parseInt(selectedSiteId))
                    ?.name
            }
          />
          {selectedSplashRow && (
            <SplashCustomizationModal
              isOpen={showSplashModal}
              toggle={toggleSplashModal}
              space={selectedSplashRow}
              onUpdate={() => {
                // Refresh spaces data after update
                if (selectedSiteId && selectedSiteId !== "All") {
                  getSpaces(selectedSiteId, {
                    page: currentPage,
                    perPage: 10,
                    search: searchTerm,
                  });
                } else if (selectedSiteId === "All") {
                  fetchAllSpaces({
                    page: currentPage,
                    perPage: 10,
                    search: searchTerm,
                  });
                }
              }}
            />
          )}

          {/* Hidden QR codes for download - only rendered when downloading */}
          {allFilteredSpaces?.length > 0 && (
            <div
              style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
            >
              {allFilteredSpaces.map((space) => (
                <div
                  key={`qr-download-${space.space_id}`}
                  ref={(el) => (qrRefs.current[space.space_id] = el)}
                >
                  <QRCode
                    value={`https://qr.clickorder.io/menu/${space.qrKey}`}
                    size={512}
                    eyeRadius={10}
                    ecLevel="H"
                    bgColor="transparent"
                    enableCORS={true}
                    crossOrigin="anonymous"
                  />
                </div>
              ))}
            </div>
          )}
          <PlanUpgradeModal
            isOpen={showUpgradeModal}
            toggle={toggleUpgradeModal}
          />
        </>
      )}
    </>
  );
};

export default Spaces;
